import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Check, RefreshCw, X } from 'lucide-react';

const RandomCodeVerify = ({ onVerify }) => {
  const [code, setCode] = useState('');
  const [inputCode, setInputCode] = useState('');
  const [verified, setVerified] = useState(false);
  // 使用ref来防止不必要的状态更新和重新渲染
  const verifiedRef = useRef(false);

  // 生成随机验证码 - 只使用更容易区分的字符
  const generateCode = useCallback(() => {
    // 使用易于辨认的字符，去除容易混淆的字符如0和O，1和l
    const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz';
    let result = '';
    for (let i = 0; i < 4; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    // // console.log('生成的验证码:', result); // 调试用，生产环境可移除
    return result;
  }, []);

  // 初始化验证码 - 仅在组件挂载时执行一次
  useEffect(() => {
    const newCode = generateCode();
    setCode(newCode);
  }, [generateCode]);

  // 验证码输入处理 - 修复比较逻辑
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputCode(value);
    
    // 不区分大小写进行比较
    if (value.toLowerCase() === code.toLowerCase()) {
      // // console.log('验证码匹配成功!'); // 调试用
      setVerified(true);
      verifiedRef.current = true; // 更新ref值
      if (onVerify) onVerify(true);
    } else if (value.length >= code.length) {
      // 当输入长度足够但不匹配时
      // // console.log('验证码不匹配'); // 调试用
      setVerified(false);
      verifiedRef.current = false; // 更新ref值
      if (onVerify) onVerify(false);
    }
  };

  // 手动刷新验证码 - 用户点击刷新按钮
  const handleRefresh = () => {
    // 无论状态如何，强制刷新验证码
    verifiedRef.current = false;
    const newCode = generateCode();
    setCode(newCode);
    setInputCode('');
    setVerified(false);
    if (onVerify) onVerify(false);
  };

  return (
    <div className="captcha-field">
      <label>验证码</label>
      <div className="captcha-field__row">
        <input
          type="text"
          value={inputCode}
          onChange={handleInputChange}
          placeholder="输入验证码"
          maxLength={4}
          disabled={verified}
          className={verified ? 'is-verified' : ''}
        />
        <div className="captcha-field__challenge">
          <div 
            className={`captcha-code ${verified ? 'is-verified' : ''}`}
            title={`验证码: ${code}`}
          >
            <div className="captcha-code__value">
              {code.split('').map((char, index) => {
                // 验证成功后不再旋转字符
                const rotation = verified ? 0 : Math.random() * 30 - 15;
                return (
                  <span 
                    key={index} 
                    style={{ 
                      display: 'inline-block',
                      transform: `rotate(${rotation}deg)`,
                      marginLeft: index > 0 ? '2px' : 0
                    }}
                  >
                    {char}
                  </span>
                );
              })}
            </div>
            {!verified && (
              <div className="captcha-code__noise">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div 
                    key={index} 
                    className="captcha-code__line"
                    style={{
                      height: `${Math.random() * 6 + 1}px`,
                      width: '100%',
                      left: 0,
                      top: `${Math.random() * 100}%`,
                      transform: `rotate(${Math.random() * 6 - 3}deg)`
                    }}
                  />
                ))}
                {Array.from({ length: 5 }).map((_, index) => (
                  <div 
                    key={`dot-${index}`} 
                    className="captcha-code__dot"
                    style={{
                      height: `${Math.random() * 3 + 2}px`,
                      width: `${Math.random() * 3 + 2}px`,
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            className="captcha-refresh"
            title="刷新验证码"
            aria-label="刷新验证码"
          >
            <RefreshCw />
          </button>
        </div>
      </div>
      {verified && (
        <div className="captcha-message captcha-message--verified">
          <Check aria-hidden="true" />
          验证通过
        </div>
      )}
      {!verified && inputCode.length >= code.length && inputCode.length > 0 && (
        <div className="captcha-message captcha-message--error">
          <X aria-hidden="true" />
          验证码错误，请重新输入
        </div>
      )}
    </div>
  );
};

export default RandomCodeVerify;
